import { createSignal, onMount, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Fingerprint } from "@fleet/icons";

import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui/card";
import { Switch } from "@fleet/ui/switch";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { isTauriRuntime } from "@/lib/platform";
import {
  authenticateBiometric,
  checkBiometricAvailable,
  isAppLockEnabled,
  setAppLockEnabled,
} from "@/lib/app-lock";

/**
 * OPTIONAL biometric app-lock toggle (Phase 3).
 *
 * Rendered ONLY on native (`isTauriRuntime()`) — the biometric plugin is
 * Tauri-mobile only, so on plain web this component renders nothing. On a native
 * target where biometrics aren't available (e.g. desktop Tauri, or a device with
 * none enrolled) it shows a "not available on this platform" note instead of the
 * toggle.
 *
 * Enabling runs a confirmation `authenticate()` first, so the user proves the
 * prompt works before arming — they can never lock themselves out with a device
 * that won't prompt. The setting is persisted per-device (see `@/lib/app-lock`),
 * NOT synced to the account.
 */
export function AppLockSection() {
  const { t } = useI18n();
  // `null` = still probing; `true`/`false` = biometric availability resolved.
  const [available, setAvailable] = createSignal<boolean | null>(null);
  const [enabled, setEnabled] = createSignal(isAppLockEnabled());
  const [busy, setBusy] = createSignal(false);

  onMount(() => {
    void (async () => {
      setAvailable(await checkBiometricAvailable());
    })();
  });

  const toggle = (next: boolean) => {
    void (async () => {
      setBusy(true);
      try {
        if (next) {
          // Prove biometrics work BEFORE arming, so the user can never lock
          // themselves out with a device that won't prompt.
          const ok = await authenticateBiometric(t(msg`Confirm to enable app lock`));
          if (!ok) {
            showToast(t(msg`Could not verify. App lock was not enabled.`), "error");
            return;
          }
        }
        setAppLockEnabled(next);
        setEnabled(next);
        showToast(next ? t(msg`App lock enabled`) : t(msg`App lock disabled`), "success");
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Show when={isTauriRuntime()}>
      <Card>
        <CardHeader class="pb-2">
          <div class="flex items-center gap-2">
            <Fingerprint class="h-5 w-5 text-primary" />
            <CardTitle>{t(msg`App lock`)}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Show
            when={available() !== false}
            fallback={
              <p class="text-sm text-muted-foreground" data-testid="app-lock-unavailable">
                {t(msg`Not available on this platform.`)}
              </p>
            }
          >
            <div class="flex items-center justify-between gap-4">
              <div class="space-y-1">
                <p class="text-sm font-medium">{t(msg`Require biometrics to open the app`)}</p>
                <p class="text-sm text-muted-foreground">
                  {t(
                    msg`Ask for your fingerprint, face, or device PIN each time the app opens or returns to the foreground.`,
                  )}
                </p>
              </div>
              <Switch
                checked={enabled()}
                disabled={busy() || available() === null}
                onCheckedChange={toggle}
                data-testid="app-lock-toggle"
              />
            </div>
          </Show>
        </CardContent>
      </Card>
    </Show>
  );
}
